import { useState } from 'react';

import { ROUTES } from '@repo/common';
import { NotificationType } from '@repo/frontend-shared';
import { AdminVerticalLogoWithTextIcon } from '@repo/ui/assets/icons/brandIcons/AdminVerticalLogoWithTextIcon';
import type { TabType } from '@repo/ui/components/HeaderNotificationsSection/NotificationTabs';
import { Header } from '@repo/ui/fixtures/header/components/Header/Header';
import { HeaderLinkButton } from '@repo/ui/fixtures/header/components/HeaderLinkButton/HeaderLinkButton';
import type { LinkButton } from '@repo/ui/fixtures/header/types/menu-items';
import { useFeatureFlag } from '@repo/ui/hooks/use-feature-flag';

import type { NotificationFilters } from '../../hooks/use-in-app-notifications';
import { useInAppNotifications } from '../../hooks/use-in-app-notifications';
import { CommunityIcon } from '../assets/CommunityIcon';
import { SearchIcon } from '../assets/SearchIcon';
import { AdminHeaderPostNavActions } from './adminHeaderPostNavActions';

const navLinks: LinkButton[] = [
  {
    text: 'Community',
    icon: <CommunityIcon />,
  },
];

type FilterType = 'all' | 'unread-only' | 'archived';

function getFiltersForTab(selectedTab: TabType): Partial<NotificationFilters> {
  if (selectedTab === 'All') return {};
  if (selectedTab === 'Alerts') return { type: NotificationType.SYSTEM };
  return { type: NotificationType.ACHIEVEMENT };
}

function getFiltersForNotificationFilter(filterType: FilterType): Partial<NotificationFilters> {
  if (filterType === 'all') return { isRead: undefined, isArchived: undefined };
  if (filterType === 'unread-only') return { isRead: false };
  return { isArchived: true };
}

export function AdminHeader() {
  const isSearchBoxEnabled = useFeatureFlag('search_box');
  const isNotificationsEnabled = useFeatureFlag('notifications');

  const [selectedTab, setSelectedTab] = useState<TabType>('All');
  const [filterType, setFilterType] = useState<FilterType>('all');

  const tabFilters = getFiltersForTab(selectedTab);
  const notificationFilters = getFiltersForNotificationFilter(filterType);
  const combinedFilters: NotificationFilters = { ...tabFilters, ...notificationFilters };

  const {
    notifications,
    unreadCount,
    tabCounts,
    loadMore,
    hasMore,
    markAllAsRead,
    archiveAll,
    markAsRead,
    markAsUnread,
    archive,
    unarchive,
    refetch,
  } = useInAppNotifications(combinedFilters);

  const postNavActions = (
    <AdminHeaderPostNavActions
      isNotificationsEnabled={isNotificationsEnabled}
      notifications={notifications}
      unreadCount={unreadCount}
      tabCounts={tabCounts}
      onTabChange={setSelectedTab}
      onFilterChange={setFilterType}
      onLoadMore={loadMore}
      hasMore={hasMore}
      onMarkAllAsRead={markAllAsRead}
      onArchiveAll={archiveAll}
      onMarkAsRead={markAsRead}
      onMarkAsUnread={markAsUnread}
      onArchive={archive}
      onUnarchive={unarchive}
      onNotificationsOpen={() => {
        void refetch();
      }}
    />
  );

  return (
    <Header
      adminLogoIcon={AdminVerticalLogoWithTextIcon}
      navLinks={navLinks}
      logoHref={ROUTES.DASHBOARD}
      preNavLinkDesktopContent={
        isSearchBoxEnabled ? <HeaderLinkButton text="Search" icon={<SearchIcon />} /> : undefined
      }
      postNavLinkDesktopContent={postNavActions}
      postNavLinkMobileContent={postNavActions}
    />
  );
}
